// src/Components/SubscriptionPopup.jsx
import React, { useRef, useEffect, useState } from 'react';
import { X, Crown, Check, ExternalLink } from 'lucide-react';
import { useUser } from '../context/userContext';

const SubscriptionPopup = ({ 
  isOpen, 
  onClose, 
  subscriptionInfo: initialSubscriptionInfo,
  loading = false
}) => {
  const popupRef = useRef(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(initialSubscriptionInfo);
  
  // Получаем данные пользователя из контекста
  const { 
    id: userId, 
    premium, 
    telegramData,
    loading: userLoading,
    subscriptionData: contextSubscriptionData
  } = useUser();

  // Ссылка на лендинг с ценами
  const LANDING_URL = "https://artflow.eu.com/inner-art";
  
  // Ссылка на бота для службы заботы
  const BOT_USERNAME = "innerart_bot";
  const BOT_LINK = `https://t.me/${BOT_USERNAME}`;

  // Используем premium из контекста
  const effectivePremium = premium;

  // Обработка кликов вне попапа
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Обновляем subscriptionInfo
  useEffect(() => {
    if (initialSubscriptionInfo) {
      setSubscriptionInfo(initialSubscriptionInfo);
    } else {
      setSubscriptionInfo(prev => ({
        ...prev,
        active: effectivePremium,
        userId: userId,
        ...(contextSubscriptionData && {
          stripeCustomerId: contextSubscriptionData.stripeCustomerId,
          planName: contextSubscriptionData.planName,
          planId: contextSubscriptionData.planId,
          price: contextSubscriptionData.price || prev.price,
          currency: contextSubscriptionData.currency || '€',
          currentPeriodEnd: contextSubscriptionData.currentPeriodEnd,
          cancelAtPeriodEnd: contextSubscriptionData.cancelAtPeriodEnd
        }),
        ...(telegramData && {
          telegramId: telegramData.id,
          telegramUsername: telegramData.username
        })
      }));
    }
  }, [initialSubscriptionInfo, effectivePremium, userId, telegramData, contextSubscriptionData]);

  // Автоматически обновляем статус премиума из контекста
  useEffect(() => {
    if (subscriptionInfo.active !== effectivePremium) {
      setSubscriptionInfo(prev => ({
        ...prev,
        active: effectivePremium
      }));
    }
  }, [effectivePremium]);

  // Открыть лендинг с ценами
  const openLanding = () => {
    window.open(LANDING_URL, '_blank', 'noopener,noreferrer');
  };

  // Если попап закрыт или идет загрузка - не рендерим
  if (!isOpen || loading || userLoading) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div 
        ref={popupRef}
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl"
        style={{
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        {/* Заголовок - компактный */}
        <div className="px-5 pt-5 pb-2 relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
              <Crown className="w-4 h-4 text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {subscriptionInfo.active ? 'PRO активна' : 'PRO подписка'}
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-full bg-gray-100 active:bg-gray-200 transition-colors"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Контент */}
        <div className="px-5 pb-5">
          
          {!subscriptionInfo.active ? (
            <>
              {/* Кнопка выбора доступа - одна главная кнопка */}
              <button
                onClick={openLanding}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 active:from-purple-800 active:to-pink-800 text-white font-semibold py-4 px-4 rounded-xl transition-all transform active:scale-[0.98] shadow-lg mb-4 flex items-center justify-center gap-2"
              >
                <Crown className="w-5 h-5" />
                <span>Выбрать доступ</span>
                <ExternalLink className="w-4 h-4" />
              </button>

              {/* Блок с ботом - для помощи с оплатой */}
              <div className="bg-blue-50 rounded-xl p-3 mb-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center shadow-sm">
                  <span className="text-white text-lg">🤖</span>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Нужна помощь с оплатой?</p>
                  <p className="text-[10px] text-gray-500">Напишите нам в боте</p>
                </div>
                <button
                  onClick={() => window.open(BOT_LINK, '_blank', 'noopener,noreferrer')}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1"
                >
                  <span>Написать</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              
              {/* ID пользователя - супер компактно */}
              {userId && (
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-2">
                  <span className="text-[10px] text-gray-500">Ваш ID</span>
                  <span className="text-[10px] font-mono font-medium text-gray-700 bg-white px-1.5 py-0.5 rounded border border-gray-200">
                    {userId}
                  </span>
                </div>
              )}
            </>
          ) : (
            // Активная подписка - компактно
            <div className="bg-green-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Подписка активна</p>
                  <p className="text-xs text-gray-600">
                    {telegramData?.username ? `@${telegramData.username}` : 'PRO пользователь'}
                  </p>
                </div>
                <button
                  onClick={() => window.open(BOT_LINK, '_blank', 'noopener,noreferrer')}
                  className="text-xs text-blue-600 font-medium flex items-center gap-1"
                >
                  Управление
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              
              {subscriptionInfo.currentPeriodEnd && (
                <div className="mt-2 pt-2 border-t border-green-200 flex justify-between items-center">
                  <span className="text-[10px] text-gray-600">Действует до</span>
                  <span className="text-xs font-medium text-gray-900">
                    {new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString('ru-RU')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Преимущества - сетка 2x2 */}
          <div className="grid grid-cols-2 gap-1.5 mt-3">
            {subscriptionInfo.features.slice(0, 4).map((feature, index) => (
              <div key={index} className="flex items-start gap-1.5 bg-gray-50 rounded-lg p-2">
                <div className="w-1 h-1 rounded-full bg-purple-500 mt-1.5 flex-shrink-0"></div>
                <span className="text-[10px] text-gray-700 leading-tight line-clamp-2">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSS анимации */}
      <style jsx>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        @media (min-width: 640px) {
          .animate-slide-up {
            animation: fadeIn 0.2s ease-out;
          }
        }
      `}</style>
    </div>
  );
};

// Пропсы по умолчанию
SubscriptionPopup.defaultProps = {
  subscriptionInfo: {
    active: false,
    stripeCustomerId: null,
    plan: 'PRO',
    price: 55,
    currency: '€',
    period: 'в месяц',
    features: [
    ]
  },
  loading: false
};

export default SubscriptionPopup;